import { useState } from "react";
import { P, FF } from "../../styles/tokens";
import { fmt } from "../../utils/format";
import { catIcon, catColor } from "../../constants/categories";
import { isFijoVencido, isFijoPorVencer } from "../../utils/gastos";
import TDCPorPagar from "./TDCPorPagar";
import { useFinancial } from "../../contexts/FinancialContext";
import { useNavigation } from "../../contexts/NavigationContext";
import { useUI } from "../../contexts/UIContext";
import { isRecurrenteGhost } from "../../hooks/useMonthData";

function filterByPeriod(items, periodFilter, diaCobro, today) {
  if (periodFilter === "mes") return items;
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (periodFilter === "semana") {
    const weekAgo = new Date(todayDate);
    weekAgo.setDate(weekAgo.getDate() - 7);
    return items.filter(item => {
      if (!item.fecha) return true;
      const d = new Date(item.fecha + "T00:00:00");
      return d >= weekAgo && d <= todayDate;
    });
  }
  if (periodFilter === "cobro") {
    const dia = diaCobro || 15;
    let lastCobro;
    if (todayDate.getDate() >= dia) {
      lastCobro = new Date(todayDate.getFullYear(), todayDate.getMonth(), dia);
    } else {
      lastCobro = new Date(todayDate.getFullYear(), todayDate.getMonth() - 1, dia);
    }
    return items.filter(item => {
      if (!item.fecha) return true;
      const d = new Date(item.fecha + "T00:00:00");
      return d >= lastCobro && d <= todayDate;
    });
  }
  return items;
}

export default function ResumenMes() {
  const {
    month, totalIng, totalFij, totalExt, ahorroReal, flujo, flujoNeto,
    deudaTDC, totalDeudaTDCGlobal, saldoEfectivo, saldoDebito,
    capitalDisponible, hasCuentas, creditoIds,
    pieData, cuentas, transferencias, today, diaCobro,
    comprometido, alertaTDC, flujoDisponible,
    totalFijSinTDC, totalExtSinTDC, year,
  } = useFinancial();
  const { activeMonth, setSection, periodFilter, setPeriodFilter } = useNavigation();
  const { mobile, movTab, setMovTab } = useUI();

  const [tdcExpanded, setTdcExpanded] = useState(false);
  const [showAllMov, setShowAllMov] = useState(false);
  const [showPeriodFilter, setShowPeriodFilter] = useState(false);
  const [expandedBreakdown, setExpandedBreakdown] = useState(null);

  const filteredIngresos = filterByPeriod(month.ingresos, periodFilter, diaCobro, today);
  const filteredExtras = filterByPeriod(month.extras, periodFilter, diaCobro, today);
  const isFiltered = periodFilter !== "mes";
  const fTotalIng = isFiltered ? filteredIngresos.reduce((s, i) => s + (Number(i.monto) || 0), 0) : totalIng;
  const fTotalExt = isFiltered ? filteredExtras.reduce((s, e) => s + (Number(e.monto) || 0), 0) : totalExt;

  const fijosUrgentes = month.fijos.filter(f => f.monto > 0 && (isFijoVencido(f, activeMonth, year) || isFijoPorVencer(f, activeMonth, year)));
  const yr = new Date().getFullYear();
  const txFijos = month.fijos.filter(f => f.monto > 0 && f.pagado).map(f => ({
    id: f.id, name: f.concepto, sub: `Fijo${f.pagado ? "" : " · Pendiente"}`,
    amt: -(f.pagado && f.montoPagado != null ? Number(f.montoPagado) : Number(f.monto)),
    icon: "🏠", iconBg: P.fijos.pastel, type: "fijo",
    pagado: f.pagado,
    fecha: f.fechaLimite ? `${yr}-${String(activeMonth + 1).padStart(2, "0")}-${String(f.fechaLimite).padStart(2, "0")}` : null,
    badge: f.pagado ? "✓ Pagado" : isFijoVencido(f, activeMonth, year) ? "⚠ Vencido" : isFijoPorVencer(f, activeMonth, year) ? "⏰ Hoy" : "Pendiente",
    badgeToken: f.pagado ? P.ingresos : isFijoVencido(f, activeMonth, year) ? P.danger : isFijoPorVencer(f, activeMonth, year) ? P.warn : null,
  }));
  const txExtras = (isFiltered ? filteredExtras : month.extras).filter(e => e.monto > 0).map(e => ({
    id: e.id + "e", name: e.concepto, sub: e.cat || "Extra",
    amt: -e.monto, icon: catIcon(e.cat), iconBg: `${catColor(e.cat)}18`, type: "extra",
    fecha: e.fecha || null,
    badge: e.cat || "Extra", badgeToken: null,
  }));
  const ingrTx = (isFiltered ? filteredIngresos : month.ingresos).filter(i => i.monto > 0).map(i => ({
    id: i.id + "i", name: i.concepto, sub: "Ingreso",
    amt: i.monto, icon: "💵", iconBg: P.ingresos.pastel, type: "ingreso",
    fecha: i.fecha || null,
  }));

  const cuentaName = (id) => {
    const c = cuentas.find(ct => ct.id === id);
    return c ? c.nombre : "Cuenta";
  };
  const retirosTx = (transferencias || [])
    .filter(t => {
      if (!t.created_at) return false;
      const d = new Date(t.created_at);
      return d.getMonth() === activeMonth && d.getFullYear() === new Date().getFullYear();
    })
    .map(t => ({
      id: t.id + "t", name: t.concepto || "Transferencia",
      sub: `${cuentaName(t.cuenta_origen)} → ${cuentaName(t.cuenta_destino)}`,
      amt: t.monto, icon: "🔄", iconBg: `${P.transfer.base}18`, type: "retiro", fecha: t.created_at ? t.created_at.slice(0, 10) : null,
    }));

  const movTabs = [
    { key: "todos", label: "Todos" },
    { key: "gastos", label: "Gastos" },
    { key: "ingresos", label: "Ingresos" },
    { key: "retiros", label: "Retiros" },
  ];
  const gastosTx = [...txFijos, ...txExtras];
  const rawTx =
    movTab === "gastos" ? gastosTx :
    movTab === "ingresos" ? ingrTx :
    movTab === "retiros" ? retirosTx :
    [...ingrTx, ...gastosTx, ...retirosTx];

  // Ordenar por fecha (reciente primero), sin fecha al final
  const sortedTx = [...rawTx].sort((a, b) => {
    if (!a.fecha && !b.fecha) return 0;
    if (!a.fecha) return 1;
    if (!b.fecha) return -1;
    return b.fecha.localeCompare(a.fecha);
  });

  // Agrupar por fecha
  const groupedTx = [];
  let lastDate = null;
  sortedTx.forEach(tx => {
    const dateKey = tx.fecha || "_sin_fecha";
    if (dateKey !== lastDate) {
      groupedTx.push({ type: "header", date: dateKey });
      lastDate = dateKey;
    }
    groupedTx.push(tx);
  });

  const MOV_LIMIT = 8;
  const visibleTx = showAllMov ? groupedTx : groupedTx.slice(0, MOV_LIMIT);

  const formatDateHeader = (d) => {
    if (d === "_sin_fecha") return "Sin fecha";
    const date = new Date(d + "T12:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(date);
    target.setHours(0, 0, 0, 0);
    const diffDays = Math.round((today - target) / 86400000);
    if (diffDays === 0) return "Hoy";
    if (diffDays === 1) return "Ayer";
    const day = date.getDate();
    const monthName = date.toLocaleString("es-MX", { month: "long" });
    return `${day} de ${monthName}`;
  };

  // Empty state: sin ingresos ni gastos registrados
  if (totalIng === 0 && month.fijos.length === 0 && month.extras.length === 0) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 24px", textAlign: "center", background: P.surface, borderRadius: 20, border: `1.5px solid ${P.border}` }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🐿️</div>
        <div style={{ fontSize: 16, fontWeight: 800, color: P.text, fontFamily: FF, marginBottom: 8 }}>¡Hola!</div>
        <div style={{ fontSize: 13, color: P.textSm, fontFamily: FF, lineHeight: 1.6, marginBottom: 20, maxWidth: 260 }}>
          Registra tu primer ingreso o gasto para ver cómo va tu mes.
        </div>
        <button
          onClick={() => setSection("editar")}
          style={{ padding: "10px 24px", borderRadius: 12, border: "none", background: "#143D2E", color: "#fff", fontSize: 13, fontWeight: 700, fontFamily: FF, cursor: "pointer" }}
        >
          Registrar
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

      {/* ═══ BLOQUE TDC CONSOLIDADO (solo si hay deuda sin pagar) ═══ */}
      {(alertaTDC || deudaTDC.filter(t => !t.pagada).length > 0) && (
        <div style={{ borderRadius: 16, overflow: "hidden", border: `1.5px solid ${alertaTDC ? (alertaTDC.nivel === "critico" ? "#FECACA" : "#FDE68A") : "#F59E0B"}` }}>
          {/* Alerta header */}
          {alertaTDC && (() => {
            const isCritico = alertaTDC.nivel === "critico";
            const bg = isCritico ? "#FEF2F2" : "#FFFBEB";
            const titleColor = isCritico ? "#991B1B" : "#92400E";
            const textColor = isCritico ? "#DC2626" : "#B45309";
            const emoji = isCritico ? "🚨" : "⚠️";
            return (
              <div
                onClick={() => setTdcExpanded(!tdcExpanded)}
                style={{ padding: "12px 16px", background: bg, cursor: "pointer", display: "flex", alignItems: "flex-start", gap: 10 }}
              >
                <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{emoji}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: titleColor, fontFamily: FF, marginBottom: 3 }}>
                    {isCritico ? "No te alcanza para cubrir la TDC" : "Pagar tu TDC te dejaría con menos del 20% de tu sueldo"}
                  </div>
                  <div style={{ fontSize: 10, color: textColor, fontFamily: FF, lineHeight: 1.5 }}>
                    {isCritico ? (
                      <>Debes <strong>{fmt(deudaTDC.filter(t => !t.pagada).reduce((s, t) => s + t.totalDeuda, 0))}</strong> pero tienes <strong>{fmt(alertaTDC.disponible)}</strong>. Faltan <strong>{fmt(alertaTDC.faltante)}</strong>.</>
                    ) : (
                      <>Si pagas <strong>{fmt(deudaTDC.filter(t => !t.pagada).reduce((s, t) => s + t.totalDeuda, 0))}</strong>, te quedan <strong>{fmt(alertaTDC.despuesDePagar)}</strong> ({alertaTDC.pctRestante.toFixed(0)}%){alertaTDC.diasHastaLimite != null ? <> · {alertaTDC.diasHastaLimite} días al límite</> : null}</>
                    )}
                  </div>
                </div>
                <span style={{ fontSize: 12, color: titleColor, flexShrink: 0, marginTop: 4, transform: tdcExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s" }}>▼</span>
              </div>
            );
          })()}

          {/* Encabezado si no hay alerta pero sí deuda sin pagar */}
          {!alertaTDC && deudaTDC.filter(t => !t.pagada).length > 0 && (() => {
            const sinPagar = deudaTDC.filter(t => !t.pagada);
            const todayDay = today.getDate();
            const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
            // Días hasta la fecha límite más cercana
            let diasMin = null;
            for (const t of sinPagar) {
              if (!t.fechaLimitePago) continue;
              const lim = Number(t.fechaLimitePago);
              const d = lim >= todayDay ? lim - todayDay : (daysInMonth - todayDay) + lim;
              if (diasMin === null || d < diasMin) diasMin = d;
            }
            return (
              <div
                onClick={() => setTdcExpanded(!tdcExpanded)}
                style={{ padding: "12px 16px", background: "#FFFBEB", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 16 }}>💳</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#92400E", fontFamily: FF }}>Por pagar en TDC</div>
                    <div style={{ fontSize: 10, color: "#B45309" }}>
                      {sinPagar.length} tarjeta{sinPagar.length !== 1 ? "s" : ""}
                      {diasMin !== null && ` · ${diasMin === 0 ? "Vence hoy" : diasMin < 0 ? "Vencido" : `${diasMin} día${diasMin !== 1 ? "s" : ""} al límite`}`}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 18, fontWeight: 900, color: "#92400E", fontFamily: FF }}>{fmt(sinPagar.reduce((s, t) => s + t.totalDeuda, 0))}</span>
                  <span style={{ fontSize: 12, color: "#92400E", transform: tdcExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s" }}>▼</span>
                </div>
              </div>
            );
          })()}

          {/* Detalle expandible */}
          {tdcExpanded && deudaTDC.length > 0 && (
            <div style={{ padding: "0 14px 14px", background: "#FFFDF5" }}>
              <TDCPorPagar />
            </div>
          )}
          {/* Próximo ciclo */}
          {deudaTDC.some(t => t.gastosPostCorte > 0) && (
            <div style={{ padding: "8px 14px", background: "#F0F9FF", borderTop: `1px solid #BAE6FD` }}>
              <div style={{ fontSize: 10, color: "#0369A1", fontFamily: FF, fontWeight: 600 }}>
                📋 Próximo ciclo: {fmt(deudaTDC.reduce((s, t) => s + (t.gastosPostCorte || 0), 0))} en compras después del corte
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ PRÓXIMOS PAGOS (fijos urgentes + TDC por vencer) ═══ */}
      {(() => {
        // TDCs sin pagar con fecha límite próxima (≤5 días o vencida)
        const todayDay = today.getDate();
        const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
        const tdcUrgentes = deudaTDC.filter(t => {
          if (t.pagada || !t.fechaLimitePago) return false;
          const limDay = Number(t.fechaLimitePago);
          const dias = limDay >= todayDay ? limDay - todayDay : (daysInMonth - todayDay) + limDay;
          return dias <= 5;
        }).map(t => {
          const limDay = Number(t.fechaLimitePago);
          const dias = limDay >= todayDay ? limDay - todayDay : (daysInMonth - todayDay) + limDay;
          const vencido = limDay < todayDay;
          return { ...t, dias, vencido, isTDC: true };
        });
        const allUrgentes = [
          ...fijosUrgentes.slice(0, 3).map(f => ({ ...f, isTDC: false })),
          ...tdcUrgentes,
        ];
        if (allUrgentes.length === 0) return null;
        return (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: P.text, fontFamily: FF }}>Próximos pagos</div>
              <button onClick={() => setSection("editar")} style={{ fontSize: 11, fontWeight: 600, color: P.ingresos.mid, background: "none", border: "none", cursor: "pointer", fontFamily: FF }}>Ver todo →</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {allUrgentes.map((item) => {
                if (item.isTDC) {
                  const accentColor = item.vencido ? "#DC2626" : "#F59E0B";
                  const diasText = item.vencido
                    ? `Venció día ${item.fechaLimitePago}`
                    : item.dias === 0 ? "Vence hoy" : `En ${item.dias} día${item.dias !== 1 ? "s" : ""}`;
                  return (
                    <div key={`tdc-${item.id}`} style={{ background: P.surface, borderRadius: 12, border: `1.5px solid ${P.border}`, borderLeft: `4px solid ${accentColor}`, display: "flex", alignItems: "center", gap: 10, padding: "10px 14px" }}>
                      <div style={{ width: 34, height: 34, borderRadius: 10, background: "#FEF3C7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>💳</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: P.text, fontFamily: FF, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.nombre}{item.banco ? ` · ${item.banco}` : ""}</div>
                        <div style={{ fontSize: 10, color: item.vencido ? "#DC2626" : "#B45309", fontFamily: FF, marginTop: 1, fontWeight: 500 }}>{item.vencido ? "⚠ " : "⏰ "}{diasText}</div>
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 900, color: "#92400E", fontFamily: FF, flexShrink: 0 }}>{fmt(item.totalDeuda)}</div>
                    </div>
                  );
                }
                // Fijos urgentes (original)
                const f = item;
                const vencido = isFijoVencido(f, activeMonth, year);
                const accentColor = vencido ? P.extras.base : "#D97706";
                const labelColor = vencido ? P.extras.text : P.warn.text;
                const labelBg = vencido ? P.extras.pastel : P.warn.pastel;
                const diasText = f.fechaLimite
                  ? vencido
                    ? `Venció día ${f.fechaLimite}`
                    : Number(f.fechaLimite) === today.getDate()
                      ? "Vence hoy"
                      : `En ${Number(f.fechaLimite) - today.getDate()} día${Number(f.fechaLimite) - today.getDate() !== 1 ? "s" : ""}`
                  : "Sin fecha";
                return (
                  <div key={f.id} style={{ background: P.surface, borderRadius: 12, border: `1.5px solid ${P.border}`, borderLeft: `4px solid ${accentColor}`, display: "flex", alignItems: "center", gap: 10, padding: "10px 14px" }}>
                    <div style={{ width: 34, height: 34, borderRadius: 10, background: labelBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>🏠</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: P.text, fontFamily: FF, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.concepto}</div>
                      <div style={{ fontSize: 10, color: labelColor, fontFamily: FF, marginTop: 1, fontWeight: 500 }}>{vencido ? "⚠ " : "⏰ "}{diasText}</div>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 900, color: P.text, fontFamily: FF, flexShrink: 0 }}>{fmt(f.monto)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ═══ ¿A DÓNDE VA TU DINERO? ═══ */}
      {totalIng > 0 && (() => {
        // Calcular totales: solo gastos reales (pagados para fijos, reales para extras)
        // Fijos con TDC: cuentan al comprar (ya es deuda)
        const fijosTDCItems = month.fijos.filter(f => f.cuentaId && creditoIds.has(f.cuentaId) && !isRecurrenteGhost(f));
        const fijosTDCTotal = fijosTDCItems.reduce((s, f) => s + (f.montoPagado != null ? Number(f.montoPagado) : Number(f.monto) || 0), 0);
        // Fijos sin TDC: solo los pagados (el dinero ya salió de la cuenta)
        const fijosNoTDCItems = month.fijos.filter(f => f.pagado && !(f.cuentaId && creditoIds.has(f.cuentaId)) && !String(f.id).startsWith("rec_"));
        const fijosNoTDCTotal = fijosNoTDCItems.reduce((s, f) => s + (f.montoPagado != null ? Number(f.montoPagado) : Number(f.monto) || 0), 0);
        const fijosTotal = fijosNoTDCTotal + fijosTDCTotal;

        // Extras: solo reales (no ghosts), TDC cuenta al comprar
        const extrasTDCItems = month.extras.filter(e => e.cuentaId && creditoIds.has(e.cuentaId) && !isRecurrenteGhost(e));
        const extrasTDCTotal = extrasTDCItems.reduce((s, e) => s + (Number(e.monto) || 0), 0);
        const extrasNoTDCItems = month.extras.filter(e => !isRecurrenteGhost(e) && !(e.cuentaId && creditoIds.has(e.cuentaId)));
        const extrasNoTDCTotal = extrasNoTDCItems.reduce((s, e) => s + (Number(e.monto) || 0), 0);
        const extrasTotal = extrasNoTDCTotal + extrasTDCTotal;

        const libre = Math.max(totalIng - fijosTotal - extrasTotal - ahorroReal, 0);
        const base = totalIng || 1;

        // Agrupar gastos por cuenta para el expandido
        const groupByCuenta = (items) => {
          const groups = {};
          items.forEach(item => {
            const cid = item.cuentaId || "_sin_cuenta";
            if (!groups[cid]) groups[cid] = { total: 0, cuenta: cuentas.find(c => c.id === cid) };
            const monto = item.pagado && item.montoPagado != null ? Number(item.montoPagado) : Number(item.monto) || 0;
            groups[cid].total += monto;
          });
          return Object.entries(groups).filter(([, v]) => v.total > 0).sort((a, b) => b[1].total - a[1].total);
        };

        const TIPO_ICON = { efectivo: "💵", "débito": "💳", "crédito": "💳", vales: "🎫", ahorro: "🐿️" };

        const breakdownItems = [
          { key: "fijos", label: "Gastos Fijos", total: fijosTotal, noTDC: fijosNoTDCTotal, tdc: fijosTDCTotal, colorBase: P.fijos.mid, colorTDC: P.tdc.base, icon: "🏠",
            items: [...fijosNoTDCItems, ...fijosTDCItems], expandable: true },
          { key: "extras", label: "Gastos Variables", total: extrasTotal, noTDC: extrasNoTDCTotal, tdc: extrasTDCTotal, colorBase: P.extras.mid, colorTDC: P.tdc.light, icon: "🛍️",
            items: [...extrasNoTDCItems, ...extrasTDCItems], expandable: true },
          ...(ahorroReal > 0 ? [{ key: "ahorro", label: "Ahorro", total: ahorroReal, noTDC: ahorroReal, tdc: 0, colorBase: "#10B981", colorTDC: "#10B981", icon: "💰", items: [], expandable: false }] : []),
        ].filter(i => i.total > 0);

        return (
        <div style={{ background: P.surface, borderRadius: 16, padding: "16px 18px", border: `1.5px solid ${P.border}` }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: P.text, fontFamily: FF, marginBottom: 14 }}>¿A dónde va tu dinero?</div>
          {breakdownItems.map(item => {
            const pct = Math.min((item.total / base) * 100, 100);
            const pctNoTDC = item.total > 0 ? (item.noTDC / item.total) * 100 : 100;
            const isExpanded = expandedBreakdown === item.key;
            const cuentaGroups = item.expandable ? groupByCuenta(item.items) : [];
            return (
            <div key={item.key} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 13 }}>{item.icon}</span>
                  <span style={{ fontSize: 12, color: P.textMd, fontFamily: FF }}>{item.label}</span>
                  {item.tdc > 0 && <span style={{ fontSize: 9, color: P.tdc.base, fontWeight: 700, fontFamily: FF }}>TDC {fmt(item.tdc)}</span>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 800, color: P.text, fontFamily: FF, fontVariantNumeric: "tabular-nums" }}>{fmt(item.total)}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: P.textXs, fontFamily: FF, background: P.bg, borderRadius: 99, padding: "1px 7px", border: `1px solid ${P.border}` }}>{pct.toFixed(0)}%</span>
                  {item.expandable && (
                    <button type="button" onClick={() => setExpandedBreakdown(isExpanded ? null : item.key)}
                      style={{ width: 24, height: 24, borderRadius: 8, border: `1px solid ${P.border}`, background: isExpanded ? P.ingresos.pastel : P.bg, color: isExpanded ? P.ingresos.base : P.textXs, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {isExpanded ? "−" : "+"}
                    </button>
                  )}
                </div>
              </div>
              {/* Barra bicolor: base + TDC */}
              <div style={{ height: 6, background: P.bg, borderRadius: 99, overflow: "hidden", display: "flex" }}>
                {item.noTDC > 0 && <div style={{ width: `${(item.noTDC / base) * 100}%`, background: item.colorBase, transition: "width .5s" }} />}
                {item.tdc > 0 && <div style={{ width: `${(item.tdc / base) * 100}%`, background: item.colorTDC, transition: "width .5s" }} />}
              </div>
              {/* Expandido: detalle por cuenta */}
              {isExpanded && item.expandable && (
                <div style={{ marginTop: 8, padding: "10px 12px", background: P.bg, borderRadius: 10 }}>
                  {cuentaGroups.map(([cid, { total: cTotal, cuenta }]) => (
                    <div key={cid} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 12 }}>{cuenta ? (TIPO_ICON[cuenta.tipo] || "💳") : "❓"}</span>
                        <span style={{ fontSize: 11, color: P.textSm, fontFamily: FF }}>{cuenta ? cuenta.nombre : "Sin cuenta"}</span>
                        {cuenta?.tipo === "crédito" && <span style={{ fontSize: 8, fontWeight: 700, color: P.tdc.base, background: P.tdc.pastel, borderRadius: 99, padding: "1px 5px" }}>TDC</span>}
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 800, color: P.text, fontFamily: FF, fontVariantNumeric: "tabular-nums" }}>{fmt(cTotal)}</span>
                    </div>
                  ))}
                  <button type="button" onClick={() => setSection("analisis")}
                    style={{ marginTop: 8, width: "100%", padding: "8px", borderRadius: 8, border: `1px solid ${P.ingresos.tint}`, background: P.ingresos.pastel, color: P.ingresos.text, fontFamily: FF, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                    Más detalle →
                  </button>
                </div>
              )}
            </div>
            );
          })}
        </div>
        );
      })()}

      {/* ═══ MOVIMIENTOS ═══ */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: P.text, fontFamily: FF }}>Movimientos</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button type="button" onClick={() => setSection("movimientos")}
              style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${P.ingresos.tint}`, background: P.ingresos.pastel, color: P.ingresos.base, fontSize: 11, fontWeight: 700, fontFamily: FF, cursor: "pointer" }}>
              Ver detalle →
            </button>
          <button
            aria-label="Filtros de período"
            onClick={() => setShowPeriodFilter(v => !v)}
            style={{
              width: 32, height: 32, borderRadius: 8, border: `1px solid ${showPeriodFilter ? P.ingresos.base : P.border}`,
              background: showPeriodFilter ? P.ingresos.pastel : P.surface,
              color: showPeriodFilter ? P.ingresos.base : P.textSm,
              fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all .2s",
            }}
          >
            ⚙
          </button>
          </div>
        </div>
        {showPeriodFilter && (
          <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
            {[
              { key: "semana", label: "Semana" },
              { key: "cobro", label: "Cobro" },
              { key: "mes", label: "Mes" },
            ].map(f => {
              const active = periodFilter === f.key;
              return (
                <button key={f.key} onClick={() => setPeriodFilter(f.key)} style={{
                  padding: "4px 10px", borderRadius: 8, minHeight: 32,
                  border: active ? "none" : `1px solid ${P.border}`,
                  background: active ? P.ingresos.base : "transparent",
                  color: active ? "#fff" : P.textXs,
                  fontSize: 10, fontWeight: 700, fontFamily: FF, cursor: "pointer",
                  transition: "all .2s",
                }}>
                  {f.label}
                </button>
              );
            })}
          </div>
        )}
        {/* Mov type tabs */}
        <div style={{ display: "flex", gap: 5, marginBottom: 10 }}>
          {movTabs.map(t => {
            const active = movTab === t.key;
            return (
              <button key={t.key} onClick={() => { setMovTab(t.key); setShowAllMov(false); }} style={{
                padding: "5px 12px", borderRadius: 99, border: active ? "none" : `1.5px solid ${P.border}`,
                background: active ? "#1E293B" : P.surface, color: active ? "#fff" : P.textSm,
                fontSize: 11, fontWeight: 700, fontFamily: FF, cursor: "pointer", whiteSpace: "nowrap",
                transition: "all .2s",
              }}>
                {t.label}
              </button>
            );
          })}
        </div>
        {sortedTx.length === 0 ? (
          <div style={{ textAlign: "center", padding: "28px 0", background: P.surface, borderRadius: 14, border: `1.5px solid ${P.border}` }}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>{movTab === "retiros" ? "🔄" : "🌱"}</div>
            <div style={{ fontSize: 12, color: P.textXs, fontFamily: FF }}>
              {movTab === "retiros"
                ? "No hay retiros este mes"
                : <>Usa <strong style={{ fontWeight: 700, color: P.text }}>✏️ Editar</strong> para registrar datos</>}
            </div>
          </div>
        ) : (
          <div style={{ background: P.surface, borderRadius: 14, border: `1.5px solid ${P.border}`, overflow: "hidden" }}>
            {visibleTx.map((tx, i) => {
              if (tx.type === "header") {
                return (
                  <div key={tx.date} style={{ padding: "8px 14px 4px", background: P.bg, borderBottom: `1px solid ${P.border}` }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: P.textSm, fontFamily: FF, textTransform: "capitalize" }}>{formatDateHeader(tx.date)}</span>
                  </div>
                );
              }
              return (
              <div key={tx.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: i < visibleTx.length - 1 ? `1px solid ${P.border}` : "none" }}>
                <div style={{ width: 36, height: 36, borderRadius: 11, background: tx.iconBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>{tx.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: P.text, fontFamily: FF, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tx.name}</div>
                  <div style={{ fontSize: 10, color: P.textXs, fontFamily: FF, marginTop: 1 }}>{tx.sub}</div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 900, fontFamily: FF,
                    color: tx.type === "ingreso" ? P.ingresos.text
                      : tx.type === "retiro" ? P.transfer.base
                      : tx.pagado ? P.text
                      : (tx.badgeToken === P.danger || tx.badgeToken === P.warn) ? tx.badgeToken.text : P.text
                  }}>
                    {tx.type === "ingreso" ? "+" : tx.type === "retiro" ? "" : "-"}{fmt(Math.abs(tx.amt))}
                  </div>
                  {tx.badgeToken ? (
                    <div style={{ fontSize: 8, fontWeight: 700, background: tx.badgeToken.pastel, color: tx.badgeToken.text, borderRadius: 99, padding: "1px 7px", marginTop: 2, display: "inline-block", border: `1px solid ${tx.badgeToken.tint}` }}>{tx.badge}</div>
                  ) : tx.type === "retiro" ? (
                    <div style={{ fontSize: 8, fontWeight: 600, color: P.transfer.base, marginTop: 2 }}>Retiro</div>
                  ) : tx.badge && tx.type !== "ingreso" ? (
                    <div style={{ fontSize: 8, fontWeight: 600, color: P.textXs, marginTop: 2 }}>{tx.badge}</div>
                  ) : null}
                </div>
              </div>
              );
            })}
            {/* Ver más / Ver menos */}
            {groupedTx.length > MOV_LIMIT && (
              <button
                onClick={() => setShowAllMov(!showAllMov)}
                style={{
                  width: "100%", padding: "10px", border: "none", borderTop: `1px solid ${P.border}`,
                  background: P.bg, color: P.ingresos.base, fontSize: 12, fontWeight: 700,
                  fontFamily: FF, cursor: "pointer", textAlign: "center",
                }}
              >
                {showAllMov ? "Ver menos ↑" : `Ver todos (${sortedTx.length}) →`}
              </button>
            )}
          </div>
        )}
      </div>

    </div>
  );
}
